import { useEffect, useState } from 'react';
import type { MatchHistoryItem } from '@xiangqi/protocol';
import { MODE_INFO } from '@xiangqi/protocol';
import { api, ensureSession } from '../api.ts';

interface HistoryScreenProps {
  navigate(path: string): void;
}

export function HistoryScreen({ navigate }: HistoryScreenProps) {
  const [filter, setFilter] = useState<'all' | 'win' | 'loss' | 'draw'>('all');
  const [items, setItems] = useState<MatchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ensureSession()
      .then(() => api.history(filter === 'all' ? undefined : filter))
      .then((res) => {
        if (!cancelled) {
          setItems(res.history);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Không tải được lịch sử');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <main className="page history-page">
      <header className="page-header">
        <h1>📜 Lịch sử đấu</h1>
        <p className="page-subtitle">Xem lại các ván đấu đã hoàn thành</p>
      </header>

      {/* Filter Tabs matching Figma */}
      <div className="filter-tabs">
        <button
          className={`tab-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Tất cả
        </button>
        <button
          className={`tab-btn win ${filter === 'win' ? 'active' : ''}`}
          onClick={() => setFilter('win')}
        >
          Thắng
        </button>
        <button
          className={`tab-btn loss ${filter === 'loss' ? 'active' : ''}`}
          onClick={() => setFilter('loss')}
        >
          Thua
        </button>
        <button
          className={`tab-btn draw ${filter === 'draw' ? 'active' : ''}`}
          onClick={() => setFilter('draw')}
        >
          Hòa
        </button>
      </div>

      {loading ? (
        <p className="waiting">Đang tải lịch sử đấu…</p>
      ) : error ? (
        <div className="banner error">{error}</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">♟️</span>
          <h3>Chưa có ván đấu nào</h3>
          <p>Hãy tham gia một ván cờ với bạn bè hoặc máy để ghi lại lịch sử!</p>
          <button className="primary" onClick={() => navigate('/')}>
            Chơi ngay
          </button>
        </div>
      ) : (
        <div className="history-list">
          {items.map((item) => {
            const modeLabel = MODE_INFO[item.ruleset]?.label ?? item.ruleset;
            const resultLabel =
              item.result === 'win' ? 'Thắng' : item.result === 'loss' ? 'Thua' : 'Hòa';
            const dateStr = new Date(item.ended_at).toLocaleDateString('vi-VN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });

            return (
              <div key={item.match_id} className={`history-card ${item.result}`}>
                <div className="history-card-header">
                  <span className="ruleset-badge">{modeLabel}</span>
                  <span className={`result-badge ${item.result}`}>{resultLabel}</span>
                </div>

                <div className="history-card-body">
                  <div className="opponent-info">
                    <span className="vs-label">vs</span>
                    <strong>{item.opponent_name}</strong>
                    <span className="side-hint">
                      (Bạn cầm {item.your_side === 'red' ? 'Đỏ' : 'Đen'})
                    </span>
                  </div>

                  <div className="meta-info">
                    <span>{dateStr}</span>
                    <span>· {item.ply} nước</span>
                  </div>
                </div>

                <div className="history-card-footer">
                  <button
                    className="replay-action-btn"
                    onClick={() => navigate(`/replay/${item.match_id}`)}
                  >
                    👁️ Xem lại
                  </button>
                  <button
                    className="replay-action-btn"
                    onClick={() => navigate(`/replay/${item.match_id}?review=1`)}
                    style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}
                  >
                    📊 Phân tích AI
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
